import moment from 'moment';

/**
 * Thin wrapper around the existing live GSC proxy endpoint.
 * The endpoint forwards the raw Search Analytics request body to the Cloud
 * Function using the OAuth credentials linked to `domain_id`.
 */
export const gscTestQuery = (data) => {
    return fetch('/api/v1/domains/gsc-test-query/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': window.csrf_token,
        },
        body: JSON.stringify(data),
    });
};

/**
 * Reads the currently selected date range from the global filters, falling back
 * to the last 7 days when the filters are not yet initialized.
 */
export function getFilterDates() {
    const dates = window?.filters?.filters?.dates?.values;
    const start_date = dates?.start_date?.value;
    const end_date = dates?.end_date?.value;
    return {
        startDate: start_date || moment().subtract(7, 'days').format('YYYY-MM-DD'),
        endDate: end_date || moment().format('YYYY-MM-DD'),
    };
}

/**
 * Builds the Search Analytics request body that fetches clicks/impressions by
 * date for the given page URLs. The page filter mirrors the shape used across
 * the app: a single `includingRegex` filter with the URLs joined by `|`.
 */
export function buildGscResource({ startDate, endDate, urls }) {
    return {
        aggregationType: 'AUTO',
        dataState: 'ALL',
        startDate,
        endDate,
        type: 'WEB',
        dimensions: ['date', 'page'],
        dimensionFilterGroups: [
            {
                filters: [
                    {
                        dimension: 'page',
                        operator: 'includingRegex',
                        expression: urls.join('|'),
                    },
                ],
            },
        ],
    };
}

/**
 * Fetches GSC position/clicks/impressions-by-date for the given URLs and returns a map
 * keyed by page URL:
 *   { [url]: { position: [{date, value}], clicks: [...], impressions: [...] } }.
 * Each series is sorted ascending by date.
 */
export async function fetchGscMetricsByUrl({ domainId, domainUrl, urls }) {
    if (!urls?.length || !domainId) return {};

    const { startDate, endDate } = getFilterDates();
    const resource = buildGscResource({ startDate, endDate, urls });

    const body = {
        domain_id: domainId,
        queries: [{ resource, domain: domainUrl, key: 'data' }],
        max_count: 10000,
    };

    const response = await gscTestQuery(body);
    const data = await response.json();

    if (data?.status !== 200 || !data?.results?.data?.length) {
        return {};
    }

    const byUrl = {};
    for (const row of data.results.data) {
        // keys are ordered as the requested dimensions: [date, page]
        const [date, page] = row.keys ?? [];
        if (!page) continue;
        if (!byUrl[page]) byUrl[page] = { position: [], clicks: [], impressions: [] };
        byUrl[page].position.push({ date, value: row.position ?? 0 });
        byUrl[page].clicks.push({ date, value: row.clicks ?? 0 });
        byUrl[page].impressions.push({ date, value: row.impressions ?? 0 });
    }

    for (const page of Object.keys(byUrl)) {
        byUrl[page].position.sort((a, b) => new Date(a.date) - new Date(b.date));
        byUrl[page].clicks.sort((a, b) => new Date(a.date) - new Date(b.date));
        byUrl[page].impressions.sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    return byUrl;
}
